import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from './AuthContext'
import { apiClient } from '../common/apiClient'

export interface AccessFlags {
  bills: boolean
  billsAdmin: boolean
  cafe: boolean
  cafeAdmin: boolean
  leaveAdmin: boolean
  memoAdmin: boolean
  minutesAdmin: boolean
  kpiAdmin: boolean
  roomAdmin: boolean
  adminPanel: boolean
  systemDev: boolean
  duoServices: boolean
  itManagement: boolean // Retired; retained for compatibility with cached access responses.
  directory: boolean
  staffActions: boolean
  notesAccess: boolean
  // Division_Location__c the Notes tab is limited to (allowlist entries only)
  notesLocationFilter: string | null
  // Allowlist entry that reads every note in its location scope, managers included (HR)
  notesReadAll: boolean
  wellbeingAllowance: boolean
  dqsAdmin: boolean
}

const EMPTY_ACCESS: AccessFlags = {
  bills: false,
  billsAdmin: false,
  cafe: false,
  cafeAdmin: false,
  leaveAdmin: false,
  memoAdmin: false,
  minutesAdmin: false,
  kpiAdmin: false,
  roomAdmin: false,
  adminPanel: false,
  systemDev: false,
  duoServices: false,
  itManagement: false,
  directory: false,
  staffActions: false,
  notesAccess: false,
  notesLocationFilter: null,
  notesReadAll: false,
  wellbeingAllowance: false,
  dqsAdmin: false,
}

interface UserPermissionsContextType {
  userPermissions: any
  isOverride: boolean
  isLoadingOverride: boolean
  effectiveUserEmail?: string
  loggedInUserEmail?: string
  loggedInUserTeam?: string
  setOverrideUser: (email: string) => Promise<void>
  clearOverride: () => void
  accessFlags: AccessFlags
  accessFlagsEmail: string | null
  isLoadingAccessFlags: boolean
  // Backward compatibility (deprecated - use userPermissions.Email and userPermissions.Name instead)
  overrideEmail?: string
  overrideDisplayName?: string
  setUserPermissions: (permissions: any) => void
}

const UserPermissionsContext = createContext<UserPermissionsContextType | undefined>(undefined)

// Helper to fetch user permissions from the backend (Duo Base roster + permission fields)
const fetchUserPermissions = async (email: string): Promise<any | null> => {
  try {
    const permissions = await apiClient.get<any>(
      `/api/employees/permissions/${encodeURIComponent(email)}`
    )

    // Hardcoded division leaders (not marked in Salesforce)
    const hardcodedDivisionLeaders = ['tuan@duotax.com.au']
    if (hardcodedDivisionLeaders.includes(permissions.Email?.toLowerCase())) {
      permissions.Division_Leader__c = 'true'
    }

    return permissions
  } catch (error) {
    console.error('Error fetching user permissions:', error)
    return null
  }
}

export const UserPermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const [userPermissions, setUserPermissions] = useState<any>({})
  const [isOverride, setIsOverride] = useState(false)
  // Initialize true if a View As session is persisted — prevents pages from evaluating
  // access against the logged-in user before the override restore completes.
  const [isLoadingOverride, setIsLoadingOverride] = useState(
    () => typeof window !== 'undefined' && !!localStorage.getItem('viewAsEmail')
  )
  const originalPermissionsRef = useRef<any>(null)

  // Fetch logged-in user's permissions on mount; restore View As from localStorage if set
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user?.email) return

      const permissions = await fetchUserPermissions(user.email)
      if (!permissions) return

      setUserPermissions(permissions)
      originalPermissionsRef.current = permissions

      // Restore persisted View As only for System Development users
      const saved = localStorage.getItem('viewAsEmail')
      if (saved && permissions.Team__c === 'System Development' && saved !== permissions.Email) {
        const overridePerms = await fetchUserPermissions(saved)
        if (overridePerms) {
          setUserPermissions(overridePerms)
          setIsOverride(true)
        } else {
          localStorage.removeItem('viewAsEmail')
        }
      } else if (saved) {
        localStorage.removeItem('viewAsEmail')
      }
      setIsLoadingOverride(false)
    }

    loadPermissions()
  }, [user?.email])

  // Set override user - fetches their permissions and updates state
  const setOverrideUser = useCallback(async (email: string) => {
    if (!email) return

    setIsLoadingOverride(true)
    try {
      const permissions = await fetchUserPermissions(email)
      if (permissions) {
        setUserPermissions(permissions)
        setIsOverride(true)
        localStorage.setItem('viewAsEmail', email)
      }
    } finally {
      setIsLoadingOverride(false)
    }
  }, [])

  // Clear override - restores original logged-in user's permissions
  const clearOverride = useCallback(() => {
    if (originalPermissionsRef.current) {
      setUserPermissions(originalPermissionsRef.current)
    }
    setIsOverride(false)
    localStorage.removeItem('viewAsEmail')
  }, [])

  // Access flags — single source of truth for feature-level access rules
  const [accessFlags, setAccessFlags] = useState<AccessFlags>(EMPTY_ACCESS)
  const [accessFlagsEmail, setAccessFlagsEmail] = useState<string | null>(null)
  const [isLoadingAccessFlags, setIsLoadingAccessFlags] = useState(true)
  const effectiveEmail = userPermissions.Email || user?.email

  useEffect(() => {
    if (!effectiveEmail) {
      setAccessFlags(EMPTY_ACCESS)
      setAccessFlagsEmail(null)
      setIsLoadingAccessFlags(true)
      return
    }

    let cancelled = false
    const requestedEmail = effectiveEmail
    setIsLoadingAccessFlags(true)
    setAccessFlagsEmail(null)
    apiClient
      .get<AccessFlags>(`/api/me/access?email=${encodeURIComponent(requestedEmail)}`)
      .then((r) => {
        if (!cancelled) setAccessFlags({ ...EMPTY_ACCESS, ...r })
      })
      .catch(() => {
        if (!cancelled) setAccessFlags(EMPTY_ACCESS)
      })
      .finally(() => {
        if (!cancelled) {
          setAccessFlagsEmail(requestedEmail)
          setIsLoadingAccessFlags(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [effectiveEmail])

  // Build userPermissions with backward-compatible fields
  const userPermissionsWithOverride = {
    ...userPermissions,
    // Add override flags to userPermissions for backward compatibility
    isOverride,
    overrideEmail: isOverride ? userPermissions.Email : undefined,
    overrideDisplayName: isOverride ? userPermissions.Name : undefined,
  }

  const value = {
    userPermissions: userPermissionsWithOverride,
    isOverride,
    isLoadingOverride,
    effectiveUserEmail: userPermissions.Email || user?.email,
    loggedInUserEmail: user?.email,
    loggedInUserTeam: originalPermissionsRef.current?.Team__c,
    setOverrideUser,
    clearOverride,
    accessFlags,
    accessFlagsEmail,
    isLoadingAccessFlags,
    // Backward compatibility at context level too
    overrideEmail: isOverride ? userPermissions.Email : undefined,
    overrideDisplayName: isOverride ? userPermissions.Name : undefined,
    setUserPermissions,
  }

  return <UserPermissionsContext.Provider value={value}>{children}</UserPermissionsContext.Provider>
}

export const useUserPermissions = () => {
  const context = useContext(UserPermissionsContext)
  if (context === undefined) {
    throw new Error('useUserPermissions must be used within a UserPermissionsProvider')
  }
  return context
}
